import React, { useEffect } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import {
  BodyText,
  Button,
  HStack,
  Modal,
  theme,
  useResponsive,
} from '@sg/garnish';

import { CustomizationModalContentContainer } from '../CustomizationModalContentContainer';
import { CustomizationModalHeading } from '../CustomizationModalHeading';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationNoDressingModal = (props: Props) => {
  const {
    isVisible,
    size = 'md',
    onGoBackPress: onContinuePress,
    onContinuePress: onEndPress,
    onRequestClose,
    onShow,
  } = props;

  const { formatMessage } = useIntl();
  const { match } = useResponsive();

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isVisible) return;

    onShow?.();
  }, [isVisible, onShow]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Modal
      visible={isVisible}
      size="medium"
      accessibilityLabel={formatMessage(messages.noDressingHeading)}
      onRequestClose={onRequestClose}
    >
      <CustomizationModalContentContainer size={size}>
        <CustomizationModalHeading>
          <FormattedMessage {...messages.noDressingHeading} />
        </CustomizationModalHeading>

        <BodyText sizeMatch={['16']}>
          <FormattedMessage {...messages.noDressingBody} />
        </BodyText>

        <SafeAreaView edges={match([['bottom'], undefined])}>
          <HStack
            itemsPerRow={2}
            gap={theme.spacing['4']}
            style={styles.ctaContainer}
          >
            <Button
              size={size === 'md' ? 'large' : 'large-wide'}
              style={size === 'lg' ? styles.ctaLG : undefined}
              palette="kale"
              onPress={onContinuePress}
            >
              <FormattedMessage {...messages.goBack} />
            </Button>

            <Button
              size={size === 'md' ? 'large' : 'large-wide'}
              style={size === 'lg' ? styles.ctaLG : undefined}
              palette="primary"
              onPress={onEndPress}
            >
              <FormattedMessage {...messages.continue} />
            </Button>
          </HStack>
        </SafeAreaView>
      </CustomizationModalContentContainer>
    </Modal>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  noDressingHeading: {
    defaultMessage: 'Heads up—no dressing selected',
    description: 'Customization | No Dressing | Heading',
  },
  noDressingBody: {
    defaultMessage: 'Are you sure you’d like to continue?',
    description: 'Customization | No Dressing | Body',
  },
  goBack: {
    defaultMessage: 'Go back',
    description: 'Customization | Go back',
  },
  continue: {
    defaultMessage: 'Continue',
    description: 'Customization | Continue',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  ctaContainer: {
    paddingTop: theme.spacing['2'],
  },
  ctaLG: {
    minHeight: 72,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  isVisible: boolean;
  size?: 'md' | 'lg';
  onRequestClose: () => void;
  onGoBackPress: () => void;
  onContinuePress: () => void;
  onShow?: () => void;
};
